import {
  ChevronLeft,
  FileText,
  Zap,
  Target,
  Map as MapIcon,
  ArrowRight,
  Sparkles,
  ExternalLink
} from 'lucide-react';

interface AdminPortalPageProps {
  onBackToHome: () => void;
  onLogout?: () => void;
  onNavigateSubpage: (page: 'admin-portal' | 'admin-proposal' | 'admin-diagnostic' | 'admin-golden-targets' | 'admin-map') => void;
}

export const AdminPortalPage: React.FC<AdminPortalPageProps> = ({
  onBackToHome,
  onLogout,
  onNavigateSubpage,
}) => {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-sky-500 selection:text-white flex flex-col">
      
      {/* 1. Header Navigation */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 py-3.5 px-4 sm:px-6 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          
          <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-start">
            <button
              onClick={onBackToHome}
              className="flex items-center gap-1.5 text-xs font-bold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3.5 py-2 rounded-xl transition-all cursor-pointer shadow-sm"
            >
              <ChevronLeft className="w-4 h-4 text-sky-600" />
              <span>메인 홈페이지로</span>
            </button>

            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-xl bg-slate-900 p-1 shadow flex items-center justify-center overflow-hidden shrink-0">
                <img src="/plus_sd_logo.png" alt="플러스에스디" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-base font-black text-slate-900 tracking-tight">
                    +SD 관리자 포털
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-[10px] font-black uppercase">
                    현장 영업 &amp; 계통 관제 허브
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs">
            <a
              href="https://online.kepco.co.kr"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-sky-700 font-bold flex items-center gap-1.5 transition-colors"
            >
              <span>한전 ON 전산</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>

            {onLogout && (
              <button
                type="button"
                onClick={onLogout}
                className="px-3.5 py-2 rounded-xl bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 font-bold flex items-center gap-1 transition-colors cursor-pointer"
                title="관리자 세션 종료"
              >
                <span>로그아웃</span>
              </button>
            )}
          </div>

        </div>
      </header>

      {/* 2. Hero Notice Banner */}
      <section className="bg-gradient-to-r from-sky-50 via-white to-indigo-50 border-b border-slate-200 px-4 sm:px-6 py-8">
        <div className="max-w-7xl mx-auto space-y-2 text-center sm:text-left">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-800 text-xs font-black mb-1">
            <Sparkles className="w-3.5 h-3.5" />
            <span>관리자 통합 시스템</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
            플러스에스디 관리자 전용 포털
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 max-w-2xl leading-relaxed">
            간단 견적서 작성, 한전 계통 연계 여부 확인, 추천 영업 지역 및 전국 GIS 관제 센터를 이용하실 수 있습니다.
          </p>
        </div>
      </section>

      {/* 3. The 4 Master Tool Cards Grid */}
      <main className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 w-full flex-1">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Card 1: 📄 간단 견적서 만들기 */}
          <div
            onClick={() => onNavigateSubpage('admin-proposal')}
            className="group relative bg-white hover:border-sky-400 border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col justify-between overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-36 h-36 bg-sky-500/5 rounded-full blur-3xl group-hover:bg-sky-500/10 transition-all pointer-events-none" />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="w-13 h-13 rounded-2xl bg-sky-50 border border-sky-200 text-sky-600 flex items-center justify-center text-2xl shadow-sm group-hover:scale-110 transition-transform">
                  <FileText className="w-7 h-7 text-sky-600" />
                </div>
              </div>

              <div>
                <span className="text-xs font-extrabold text-sky-600 uppercase tracking-wider block mb-1">
                  TOOL 01
                </span>
                <h2 className="text-xl sm:text-2xl font-black text-slate-900 group-hover:text-sky-600 transition-colors">
                  간단 견적서 만들기
                </h2>
              </div>

              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 text-xs">
                <p className="text-slate-600 leading-relaxed">
                  지번 주소와 설치 용량(kW), 1kW당 임대수익을 입력하여 <strong className="text-amber-600">20년 총 확정 임대수익 및 지붕 수리비 지원</strong>이 포함된 견적서를 생성하고 카톡/문자로 공유합니다.
                </p>
              </div>
            </div>

            <div className="pt-6 flex items-center justify-between text-xs font-black text-sky-600 group-hover:text-sky-700">
              <span>견적서 만들기 열기</span>
              <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                <ArrowRight className="w-4 h-4 text-sky-600" />
              </div>
            </div>
          </div>

          {/* Card 2: ⚡ 계통 여부 확인 */}
          <div
            onClick={() => onNavigateSubpage('admin-diagnostic')}
            className="group relative bg-white hover:border-emerald-400 border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col justify-between overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-36 h-36 bg-emerald-500/5 rounded-full blur-3xl group-hover:bg-emerald-500/10 transition-all pointer-events-none" />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="w-13 h-13 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-600 flex items-center justify-center text-2xl shadow-sm group-hover:scale-110 transition-transform">
                  <Zap className="w-7 h-7 text-emerald-600" />
                </div>
              </div>

              <div>
                <span className="text-xs font-extrabold text-emerald-600 uppercase tracking-wider block mb-1">
                  TOOL 02
                </span>
                <h2 className="text-xl sm:text-2xl font-black text-slate-900 group-hover:text-emerald-600 transition-colors">
                  계통 여부 확인
                </h2>
              </div>

              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 text-xs">
                <p className="text-slate-600 leading-relaxed">
                  공장 주소(지번) 입력 시 가장 가까운 154kV 변전소, 연계 배전선로(D/L), 잔여 용량 및 인입 가능 상태를 즉시 확인합니다.
                </p>
              </div>
            </div>

            <div className="pt-6 flex items-center justify-between text-xs font-black text-emerald-600 group-hover:text-emerald-700">
              <span>계통 여부 확인 열기</span>
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                <ArrowRight className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
          </div>

          {/* Card 3: 🎯 영업 추천 지역 */}
          <div
            onClick={() => onNavigateSubpage('admin-golden-targets')}
            className="group relative bg-white hover:border-amber-400 border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col justify-between overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-36 h-36 bg-amber-500/5 rounded-full blur-3xl group-hover:bg-amber-500/10 transition-all pointer-events-none" />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="w-13 h-13 rounded-2xl bg-amber-50 border border-amber-200 text-amber-600 flex items-center justify-center text-2xl shadow-sm group-hover:scale-110 transition-transform">
                  <Target className="w-7 h-7 text-amber-600" />
                </div>
              </div>

              <div>
                <span className="text-xs font-extrabold text-amber-600 uppercase tracking-wider block mb-1">
                  TOOL 03
                </span>
                <h2 className="text-xl sm:text-2xl font-black text-slate-900 group-hover:text-amber-600 transition-colors">
                  영업 추천 지역
                </h2>
              </div>

              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 text-xs">
                <p className="text-slate-600 leading-relaxed">
                  한전 선로 용량 여유가 풍부하고 지붕 임대 수요가 높은 핵심 권역별 추천 산단 및 공략 가이드를 제공합니다.
                </p>
              </div>
            </div>

            <div className="pt-6 flex items-center justify-between text-xs font-black text-amber-600 group-hover:text-amber-700">
              <span>영업 추천 지역 보기</span>
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                <ArrowRight className="w-4 h-4 text-amber-600" />
              </div>
            </div>
          </div>

          {/* Card 4: 🗺️ 한전 계통 GIS 실시간 관제센터 */}
          <div
            onClick={() => onNavigateSubpage('admin-map')}
            className="group relative bg-white hover:border-indigo-400 border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col justify-between overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-36 h-36 bg-indigo-500/5 rounded-full blur-3xl group-hover:bg-indigo-500/10 transition-all pointer-events-none" />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="w-13 h-13 rounded-2xl bg-indigo-50 border border-indigo-200 text-indigo-600 flex items-center justify-center text-2xl shadow-sm group-hover:scale-110 transition-transform">
                  <MapIcon className="w-7 h-7 text-indigo-600" />
                </div>
              </div>

              <div>
                <span className="text-xs font-extrabold text-indigo-600 uppercase tracking-wider block mb-1">
                  TOOL 04
                </span>
                <h2 className="text-xl sm:text-2xl font-black text-slate-900 group-hover:text-indigo-600 transition-colors">
                  한전 계통 GIS 실시간 관제센터
                </h2>
              </div>

              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 text-xs">
                <p className="text-slate-600 leading-relaxed">
                  대한민국 전역 154kV 변전소, 22.9kV 배전선로 궤적(Polyline), 산단 지붕 클러스터를 실시간 인터랙티브 지도로 관제합니다.
                </p>
              </div>
            </div>

            <div className="pt-6 flex items-center justify-between text-xs font-black text-indigo-600 group-hover:text-indigo-700">
              <span>GIS 관제센터 지도 열기</span>
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                <ArrowRight className="w-4 h-4 text-indigo-600" />
              </div>
            </div>
          </div>

        </div>
      </main>

      {/* 4. Bottom Footer Bar */}
      <footer className="border-t border-slate-200 bg-white py-4 px-6 text-center text-xs text-slate-500">
        <p>© 2026 +SD PLUS SD SOLAR AGENCY • 관리자 전용 시스템</p>
      </footer>

    </div>
  );
};
